import { useState } from "react";
import api from "../api/axiosInstance";
import { formatDateTime } from "../utils/formatters";
import HighlightedBulletList, { AiChangesLegend } from "./HighlightedBulletList";

const COLUMN_LABEL_STYLE = {
  fontSize: "11px",
  fontWeight: "bold",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: "8px",
};

// One column of bullets. The "optimized" column highlights every word the AI
// added compared with the original bullets (when originals are passed in).
function BulletColumn({ label, bullets, tone, originalBullets }) {
  const isOptimized = tone === "optimized";
  return (
    <div
      style={{
        border: `1px solid ${isOptimized ? "#d7ead9" : "#e0e0e0"}`,
        borderRadius: "6px",
        padding: "12px 14px",
        backgroundColor: "#fff",
      }}
    >
      <div style={{ ...COLUMN_LABEL_STYLE, color: isOptimized ? "#1e7e34" : "#5a5a5a" }}>{label}</div>
      {isOptimized ? (
        <HighlightedBulletList originalBullets={originalBullets || []} optimizedBullets={bullets} />
      ) : bullets.length === 0 ? (
        <p style={{ fontSize: "13px", color: "#8a8a8a", margin: 0 }}>No bullet points.</p>
      ) : (
        <ul style={{ fontSize: "13px", margin: 0, paddingLeft: "18px" }}>
          {bullets.map((b, i) => (
            <li key={i} style={{ marginBottom: "6px" }}>{b}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// One project: title (+ "AI-generated" badge when isGenerated), then
// Original vs Optimized bullets side by side — or just the generated
// bullets when the project was created by AI (fully highlighted, since
// every word is new).
function ProjectBlock({ project, isLast }) {
  const original = project.originalBullets || [];
  const optimized = project.optimizedBullets || [];

  return (
    <div style={{ marginBottom: isLast ? 0 : "24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
        <strong style={{ fontSize: "14px" }}>{project.title || "Untitled project"}</strong>
        {project.isGenerated && (
          <span
            style={{
              backgroundColor: "#fff8e1",
              color: "#8a6d00",
              padding: "2px 8px",
              borderRadius: "3px",
              fontSize: "11px",
              fontWeight: "bold",
            }}
          >
            AI-GENERATED
          </span>
        )}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "14px",
          marginTop: "10px",
          alignItems: "stretch",
        }}
      >
        {project.isGenerated ? (
          <BulletColumn label="Generated" bullets={optimized} tone="optimized" originalBullets={[]} />
        ) : (
          <>
            <BulletColumn label="Original" bullets={original} tone="original" />
            <BulletColumn label="Optimized" bullets={optimized} tone="optimized" originalBullets={original} />
          </>
        )}
      </div>
    </div>
  );
}

// Projects tab on the optimization page. Runs Project Sync
// (POST /optimization/:id/projects) and shows the result. The final resume
// reuses these saved projects, so what's shown here is what goes in the file.
export default function ProjectsSection({ opt, optimizationId, onGenerated }) {
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const projects = opt.projects || [];
  const hasProjects = projects.length > 0;
  const anyGenerated = projects.some((p) => p.isGenerated);

  const handleGenerate = async () => {
    setGenerating(true);
    setErrorMessage("");
    try {
      await api.post(`/optimization/${optimizationId}/projects`);
      if (onGenerated) await onGenerated();
    } catch (err) {
      setErrorMessage(err.response?.data?.message || "Projects could not be generated. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "12px",
        }}
      >
        <p style={{ color: "#5a5a5a", fontSize: "13px", margin: 0, flex: "1 1 420px" }}>
          Adds the skills checked "Use in Optimized Resume" (and confirmed overrides) into the candidate's own
          projects. If the master resume has no Projects section, AI creates 2 projects from the candidate's real
          skills. The final resume uses exactly the projects shown here, so regenerate after changing skill
          selections.
        </p>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          style={{ padding: "6px 14px", fontSize: "13px", minWidth: "190px" }}
        >
          {generating ? "Generating..." : hasProjects ? "Regenerate Projects" : "Generate Projects with AI"}
        </button>
      </div>

      {errorMessage && <div className="auth-error" style={{ marginBottom: "12px" }}>{errorMessage}</div>}

      {opt.projectsGeneratedAt && (
        <p style={{ fontSize: "12px", color: "#5a5a5a", margin: "0 0 12px" }}>
          Last generated on {formatDateTime(opt.projectsGeneratedAt)}
        </p>
      )}

      {anyGenerated && (
        <div
          style={{
            fontSize: "13px",
            backgroundColor: "#fff8e1",
            border: "1px solid #f0dca0",
            borderRadius: "4px",
            padding: "8px 12px",
            marginBottom: "12px",
          }}
        >
          The master resume had no Projects section, so these projects were <strong>created by AI</strong> from the
          candidate's real skills. Review them with the candidate before sending the resume.
        </div>
      )}

      {hasProjects && <AiChangesLegend />}

      {!hasProjects ? (
        <p style={{ color: "#5a5a5a", fontSize: "14px", margin: 0 }}>Not generated yet.</p>
      ) : (
        projects.map((p, i) => <ProjectBlock key={i} project={p} isLast={i === projects.length - 1} />)
      )}
    </>
  );
}
